import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Action,
  Selector,
  setValue,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { HomeCountrySelected } from '@nibtravel/shared/data-access/countries';
import {
  PaymentActions,
  PaymentState,
} from '@nibtravel/shared/data-access/payment';
import { PlansAction } from '@nibtravel/shared/data-access/plans';
import { QuoteConfigState } from '@nibtravel/shared/data-access/quote-config';
import { AppConfigState } from '@nibtravel/shared/util/app-config';
import {
  emptyPolicyHolder,
  emptySpecifiedItems,
  HttpErrorMessageCode,
  HttpUnprocessableEntityError,
  IAddressInput,
  IBasicOptionInput,
  IFootprintsDonationInput,
  IPolicyHoldersInput,
  IQuotePriceSummary,
  IRegulatoryDeclarationInput,
  ISpecifiedItemOptionInput,
  NibTravelHttpError,
  OptionType,
  QuoteRoute,
  QuoteStage,
  QuoteType,
} from '@nibtravel/shared/util/models';
import { PolicyHolderService } from '@nibtravel/shared/util/service';
import { extend, merge } from 'lodash-es';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, share, tap } from 'rxjs/operators';
import { UiContextActions } from '@nibtravel/shared/data-access/ui-context';
import { LogContextKey, Route } from '@nibtravel/shared/util/constants';
import { LoggerService } from '@nibtravel/shared/util/logger';
import { DomService } from '@nibtravel/shared/util/dom';
import { v4 as newGuid } from 'uuid';

import { QuoteHelperService } from '../helpers/quote.helper';
import {
  defaultQuoteState,
  IPurchaseResponse,
  IQuoteInput,
  IQuoteState,
  ITripSummary,
} from '../models';

import { QuoteDataService } from '../quote.data-service';
import { QuoteActions } from './quote.actions';
import { QuoteEffects } from './quote.effects';
import { get } from 'lodash';

@State<IQuoteState>({
  name: 'quote',
  defaults: defaultQuoteState,
})
@Injectable()
export class QuoteState {
  constructor(
    private router: Router,
    private store: Store,
    private quoteService: QuoteDataService,
    private quoteHelper: QuoteHelperService,
    private policyHolderHelper: PolicyHolderService,
    private quoteEffects: QuoteEffects,
    private logger: LoggerService,
    private domService: DomService
  ) {
    quoteEffects.init();
  }

  @Selector()
  public static quote(state: IQuoteState): IQuoteState {
    return state;
  }

  @Selector()
  public static isQuoteValid(state: IQuoteState): boolean {
    return state.validation?.isValid ?? false;
  }

  @Selector()
  public static quoteInput(state: IQuoteState): IQuoteInput {
    return state.input;
  }

  @Selector()
  public static getPolicyHolders(state: IQuoteState): IPolicyHoldersInput[] {
    return state.input.policyHolders;
  }

  @Selector()
  public static getPolicyDependentAge(state: IQuoteState): string | undefined {
    return state.input.dependentAge;
  }

  @Selector()
  public static homeCountry(state: IQuoteState): string | undefined {
    return state?.input?.homeAddress?.countryCode;
  }

  @Selector()
  public static quotePrices(state: IQuoteState): IQuotePriceSummary[] {
    return state.prices
      ? [...state.prices].sort((a, b) =>
          a.basePrice.price < b.basePrice.price ? -1 : 1
        )
      : [];
  }

  @Selector()
  public static getQuotePrice(
    state: IQuoteState
  ): (planId: number) => IQuotePriceSummary | undefined {
    return (planId: number): IQuotePriceSummary | undefined => {
      if (state.prices?.length) {
        return state.prices.find(
          (p: IQuotePriceSummary) => p.planId === planId
        );
      }
      return undefined;
    };
  }

  @Selector()
  public static policyNumber(state: IQuoteState): string | undefined {
    return state.policyNumber;
  }

  @Selector()
  public static destinationCountryCodes(state: IQuoteState): string[] {
    return state.input.destinationCountryCodes;
  }

  @Selector()
  public static quoteType(state: IQuoteState): QuoteType {
    return state.input?.quoteType;
  }

  @Selector()
  public static getTripSummary(state: IQuoteState): ITripSummary {
    return {
      policyHolders: state.input.policyHolders,
      destinationCountryCodes: state.input.destinationCountryCodes,
      departureDate: state.input.departureDate,
      coverEndDate: state.input.coverEndDate,
      homeAddress: state.input.homeAddress,
    };
  }

  /**
   * This selector gets the last segment of the route URL.
   * This value is fed by the subscription of the RouterEvents on appComponent.
   */
  @Selector()
  public static currentRoute(state: IQuoteState): QuoteRoute {
    if (state?.currentRoute === '/') {
      return QuoteRoute.Home;
    }

    const segments =
      state?.currentRoute?.split('/')?.filter((p) => p !== '') ?? [];
    return segments.length
      ? (segments[segments.length - 1] as QuoteRoute)
      : QuoteRoute.Empty;
  }

  /** Specific selector to fetch the current quote plan id */
  @Selector()
  public static planId(state: IQuoteState): number | undefined {
    return state.input?.planId;
  }

  @Selector()
  public static getRegulatoryDeclaration(
    state: IQuoteState
  ): IRegulatoryDeclarationInput[] {
    return state.input.regulatoryDeclarations;
  }

  @Selector()
  public static membershipNumber(state: IQuoteState): string | undefined {
    return state.input.externalIdentifiers?.externalMembershipNumber;
  }

  @Selector()
  public static isPurchased(state: IQuoteState): boolean {
    return !!state.isPurchased;
  }

  @Selector()
  public static getFundableProjectId(state: IQuoteState): number | undefined {
    return state?.input?.footprintsDonation?.donationProjectId;
  }

  @Selector()
  public static donationAmount(state: IQuoteState): number | undefined {
    return state?.input?.footprintsDonation?.donationAmount;
  }

  @Action(QuoteActions.SetBasicOptions)
  SetBasicOptions(
    ctx: StateContext<IQuoteState>,
    { optionInput }: QuoteActions.SetBasicOptions
  ): void {
    const state = ctx.getState();
    const existingOptions = state.input.basicOptions ?? [];
    const newOption = existingOptions.filter(
      (option) => option.optionType !== optionInput.optionType
    );
    newOption.push(optionInput);
    ctx.patchState({
      input: {
        ...state.input,
        basicOptions: newOption,
      },
    });
  }

  @Action(QuoteActions.SetSpecifiedItem)
  SetSpecifiedItem(
    ctx: StateContext<IQuoteState>,
    { itemInput }: QuoteActions.SetSpecifiedItem
  ): void {
    const state = ctx.getState();
    const input: ISpecifiedItemOptionInput =
      state.input.specifiedItemOption || emptySpecifiedItems;
    const items = [...input.specifiedItems];
    const existingIndex = items.findIndex((i) => i.guid === itemInput.guid);
    if (existingIndex > -1) {
      const existing = items[existingIndex];
      items[existingIndex] = { ...existing, ...itemInput };
    } else {
      items.push(itemInput);
    }

    const specifiedItemOption = { ...input, specifiedItems: [...items] };
    ctx.patchState({
      input: {
        ...state.input,
        specifiedItemOption: specifiedItemOption,
      },
    });
  }

  @Action(QuoteActions.DeleteSpecifiedItem)
  DeleteSpecifiedItem(
    ctx: StateContext<IQuoteState>,
    { itemInput }: QuoteActions.DeleteSpecifiedItem
  ): void {
    const state = ctx.getState();
    const input = state.input.specifiedItemOption;
    if (!input) {
      return;
    }

    const items = [...input.specifiedItems];
    const existingIndex = items.findIndex((i) => i.guid === itemInput.guid);
    if (existingIndex > -1) {
      items.splice(existingIndex, 1);
      const specifiedItemOption = items.length
        ? { ...input, specifiedItems: [...items] }
        : undefined;

      ctx.patchState({
        input: {
          ...state.input,
          specifiedItemOption: specifiedItemOption,
        },
      });
    }
  }

  @Action(QuoteActions.QqcReset)
  resetQqc(ctx: StateContext<IQuoteState>): void {
    let state = { ...ctx.getState() };
    state = {
      ...state,
      input: {
        ...state.input,
        planId: undefined,
      },
      prices: undefined,
      stage: QuoteStage.Initial,
    };
    ctx.setState(patch(state));
  }

  @Action(QuoteActions.ResetQuote)
  reset(ctx: StateContext<IQuoteState>): void {
    ctx.setState(defaultQuoteState);
  }

  @Action(QuoteActions.ResetToOriginalQuoteInput)
  resetToOriginalQuoteInput(
    ctx: StateContext<IQuoteState>,
    data: QuoteActions.ResetToOriginalQuoteInput
  ): void {
    const {
      destinationCountryCodes,
      coverEndDate,
      departureDate,
      homeAddress,
      policyHolders,
    } = data.data;
    ctx.setState(
      patch({
        input: patch({
          destinationCountryCodes,
          coverEndDate,
          departureDate,
          homeAddress,
          policyHolders,
        }),
      })
    );
  }

  @Action(QuoteActions.ResetPolicyHolders)
  resetPolicyHolders(ctx: StateContext<IQuoteState>): void {
    const state = ctx.getState().input;
    const currentAges = state.policyHolders.map((ph) => ph.age);
    if (currentAges.length) {
      const policyHolders = currentAges.map((age) => ({
        ...emptyPolicyHolder,
        age,
      }));

      ctx.setState(patch({ input: patch({ policyHolders }) }));
    }
  }

  @Action(QuoteActions.ResetHomeAddress)
  resetHomeAddress(ctx: StateContext<IQuoteState>): void {
    const state = ctx.getState().input;
    const countryCode = state.homeAddress.countryCode;
    if (countryCode) {
      const homeAddress: Partial<IAddressInput> = { countryCode };
      ctx.setState(patch({ input: patch({ homeAddress }) }));
    }
  }

  @Action(QuoteActions.UpdateAge)
  updateAge(
    ctx: StateContext<IQuoteState>,
    { id, age }: QuoteActions.UpdateAge
  ): void {
    const quote = ctx.getState();
    const policyHolders = [...quote.input.policyHolders];
    const index = policyHolders.findIndex((p) => p.guid === id);
    if (index > -1) {
      policyHolders[index] = { ...policyHolders[index], age: age };
      const result = {
        ...quote,
        input: { ...quote.input, policyHolders: policyHolders },
      };

      ctx.setState(patch(result));
    }
  }

  @Action(QuoteActions.UpdatePolicyholder)
  updatePolicyholder(ctx: StateContext<IQuoteState>): void {
    const state = ctx.getState();
    const quoteType = QuoteState.quoteType(state);
    // We should stop doing this. The state as any won't verify type
    // If an interface changes in it, then this won't pickup.
    const countryCode = state.input.homeAddress.countryCode;
    const quoteConfig = this.store.selectSnapshot((quoteConfigState) =>
      QuoteConfigState.getState(quoteConfigState.quoteConfig, quoteType)
    );
    const policyHolders = [...state.input.policyHolders];
    if (countryCode && quoteConfig) {
      const policyHolderConfig =
        quoteConfig.policyHolderAgeConfigsMap[countryCode];
      const updatedPh = this.policyHolderHelper.formatPolicyHolders(
        policyHolders,
        policyHolderConfig
      );
      ctx.setState(patch({ input: patch({ policyHolders: updatedPh }) }));
    }
  }

  @Action(QuoteActions.UpdateAmbiguity)
  updateAmbiguous(
    ctx: StateContext<IQuoteState>,
    { isDependent, guid }: QuoteActions.UpdateAmbiguity
  ): void {
    const state = ctx.getState();
    const policyHolders = this.policyHolderHelper.updateAmbiguity(
      state.input.policyHolders,
      isDependent,
      guid
    );

    ctx.setState(patch({ input: patch({ policyHolders }) }));
  }

  @Action(QuoteActions.SetPlanForQuote)
  SetPlanForQuote(
    ctx: StateContext<IQuoteState>,
    { planId }: QuoteActions.SetPlanForQuote
  ): void {
    ctx.setState(patch({ input: patch({ planId: planId }) }));
  }

  @Action(QuoteActions.SetQuoteType)
  setQuoteType(
    ctx: StateContext<IQuoteState>,
    { quoteType }: QuoteActions.SetQuoteType
  ): IQuoteState {
    let patchInput: Partial<IQuoteInput> = { quoteType: quoteType };
    if (quoteType === QuoteType.MultiTrip) {
      patchInput = { ...patchInput, coverEndDate: undefined };
    }

    return ctx.setState(patch({ input: patch(patchInput) }));
  }

  @Action(QuoteActions.SetQuoteStage)
  SetQuoteStage(
    ctx: StateContext<IQuoteState>,
    { stage }: QuoteActions.SetQuoteStage
  ): void {
    ctx.setState(patch({ stage: stage }));
  }

  @Action(QuoteActions.SetQuoteCurrentRoute)
  SetQuoteCurrentRoute(
    ctx: StateContext<IQuoteState>,
    { route }: QuoteActions.SetQuoteCurrentRoute
  ): void {
    ctx.setState(patch({ currentRoute: route }));
  }

  @Action(QuoteActions.SetMembership)
  setMembership(
    ctx: StateContext<IQuoteState>,
    { membershipNumber: externalMembershipNumber }: QuoteActions.SetMembership
  ): void {
    ctx.setState(
      patch({
        input: patch({
          externalIdentifiers: patch({ externalMembershipNumber }),
        }),
      })
    );
  }

  @Action(QuoteActions.SetDefaultCampaignCode)
  setDefaultCampaignCode(
    ctx: StateContext<IQuoteState>,
    { defaultCode }: QuoteActions.SetDefaultCampaignCode
  ): void {
    ctx.setState(patch({ input: patch({ campaignCode: defaultCode }) }));
  }

  @Action(QuoteActions.SetPartnerCode)
  setPartnerCode(
    ctx: StateContext<IQuoteState>,
    { partnerCode }: QuoteActions.SetPartnerCode
  ): void {
    ctx.setState(
      patch({
        input: patch({
          partnerCode: partnerCode,
        } as IQuoteInput),
      })
    );
  }

  @Action(HomeCountrySelected)
  countriesLoaded(ctx: StateContext<any>, { country }: HomeCountrySelected) {
    return country
      ? ctx.dispatch(new QuoteActions.SetHomeCountry(country.code))
      : EMPTY;
  }

  @Action(QuoteActions.SetHomeCountry)
  setHomeCountry(
    ctx: StateContext<IQuoteState>,
    { countryCode }: QuoteActions.SetHomeCountry
  ): void {
    ctx.setState(
      patch({ input: patch({ homeAddress: patch({ countryCode }) }) })
    );
  }

  @Action(QuoteActions.FindQuote)
  findQuote(
    ctx: StateContext<IQuoteState>,
    { brand, quoteId }: QuoteActions.FindQuote
  ) {
    const state = ctx.getState();
    return this.quoteService
      .getQuote({ brand, quoteId, stage: state.currentRoute })
      .pipe(
        tap((response) => {
          // cache prices when users click links in email
          if (get(response, 'prices', []).length > 0) {
            this.quoteService.setPreviousQuotePrices(
              response.prices as IQuotePriceSummary[]
            );
          }

          ctx.setState(patch({ ...response }));
        }),
        catchError((err) => {
          ctx.dispatch(new QuoteActions.QuoteNotFound());
          return throwError(err);
        }),
        share()
      );
  }

  @Action(QuoteActions.NavigateToQuote)
  navigateToQuote(
    ctx: StateContext<IQuoteState>,
    data: { toKosmos?: boolean }
  ): void {
    const state = ctx.getState();
    const params = this.quoteHelper.createUrlQuoteParams(state, data);
    let appUrl = this.store.selectSnapshot(AppConfigState.getAppUrl);
    if (data?.toKosmos) {
      appUrl = this.store.selectSnapshot(AppConfigState.getKosmosUrl);
    }
    this.domService.navigateTo(appUrl, params);
  }

  // TODO: There's way too much logic here. Need to simplify
  @Action(QuoteActions.GetQuote)
  getQuote(
    ctx: StateContext<IQuoteState>
  ): Observable<IQuoteState> | undefined {
    const state = ctx.getState();
    const quoteType = QuoteState.quoteType(state);
    const countryCode = QuoteState.homeCountry(state);
    const quoteConfig = this.store.selectSnapshot((quoteConfigState) => {
      return QuoteConfigState.getState(quoteConfigState.quoteConfig, quoteType);
    });
    const brandCode = this.store.selectSnapshot(AppConfigState.getBrand);
    const planId = state.input.planId;

    if (countryCode && quoteConfig) {
      // Need to set the prepared object to state so we keep some fields removed
      const quote = this.quoteHelper.prepareQuoteObject(state);
      quote.input.campaignCode =
        quote.input.campaignCode ??
        this.store.selectSnapshot(AppConfigState.campaignCode);

      if (quote.input.quoteType === 'MULTITRIP') {
        quote.input.coverEndDate = undefined;
      }

      return this.quoteService.update(quote, brandCode, planId).pipe(
        tap((response: IQuoteState) => {
          // cache plan prices in root page and not non-root page
          if (
            quote.currentRoute === '/' ||
            quote.currentRoute.includes('turnstile')
          ) {
            this.quoteService.setPreviousQuotePrices(
              response.prices as IQuotePriceSummary[]
            );
          }
          ctx.setState(patch({ ...response }));
        }),
        tap(() => {
          ctx.dispatch(new QuoteActions.LoadQuotePlans());
        }),
        catchError((err) => {
          if (err instanceof HttpUnprocessableEntityError) {
            ctx.dispatch(new QuoteActions.InvalidQuote(err.validation));
          }

          return throwError(err);
        }),
        share()
      );
    }

    return undefined;
  }

  private revertPrices(
    oldPrices: IQuotePriceSummary[],
    newPrices: IQuotePriceSummary[],
    planId: number | undefined
  ): (IQuotePriceSummary | undefined)[] {
    const newPlanPrice = newPrices?.find((price) => price.planId === planId);
    const prices = oldPrices?.map((price) => {
      if (price.planId === planId) {
        return newPlanPrice;
      }
      return price;
    });
    return prices;
  }
  @Action(QuoteActions.ResetPromoCode)
  resetPromoCode(ctx: StateContext<IQuoteState>): void {
    ctx.setState(patch({ input: patch({ campaignCode: '' }) }));
  }

  @Action(QuoteActions.ApplyPromoCode)
  applyPromoCode(
    ctx: StateContext<IQuoteState>,
    { campaignCode }: QuoteActions.ApplyPromoCode
  ): Observable<IQuoteState> | undefined {
    const state = ctx.getState();
    const brandCode = this.store.selectSnapshot(AppConfigState.getBrand);
    const planId = state.input.planId;
    // Need to set the prepared object to state so we keep some fields removed
    const quote = this.quoteHelper.prepareQuoteObject(state);
    quote.input.campaignCode = campaignCode;

    return this.quoteService.update(quote, brandCode, planId).pipe(
      tap((response) => ctx.setState(patch({ ...response }))),
      share()
    );
  }

  @Action(QuoteActions.InvalidQuote)
  invalidQuote(
    ctx: StateContext<IQuoteState>,
    { validation }: QuoteActions.InvalidQuote
  ): void {
    if (
      validation.errors?.find(
        (p) => p.code === HttpErrorMessageCode.DepartureDateTooLow
      )
    ) {
      ctx.dispatch(
        new UiContextActions.SetQqcAlertMessage(
          HttpErrorMessageCode.DepartureDateTooLow
        )
      );
    } else if (
      validation.errors?.find(
        (p) => p.code === HttpErrorMessageCode.CampaignCodeInvalid
      )
    ) {
      ctx.dispatch([
        new QuoteActions.ResetPromoCode(),
        new UiContextActions.SetQqcAlertMessage(
          HttpErrorMessageCode.CampaignCodeInvalid
        ),
      ]);
    } else {
      ctx.dispatch(new UiContextActions.SetQqcAlertMessage('quote-error'));
    }
  }

  @Action(QuoteActions.QuoteNotFound)
  quoteNotFound(ctx: StateContext<IQuoteState>): void {
    ctx.dispatch(new UiContextActions.SetQqcAlertMessage('quote-not-found'));
  }

  @Action(QuoteActions.ResetOptions)
  resetOptions(ctx: StateContext<IQuoteState>): void {
    const state = ctx.getState();
    const basicOptions: IBasicOptionInput[] = [];

    // We need to keep the state of the PMI. Even though it's an basicOptions
    // It is defined in different location from the others, so we want to keep it
    const pmi = state.input.basicOptions?.find(
      (opt) => opt.optionType === OptionType.PrivateMedicalInsurance
    );
    if (pmi) {
      basicOptions.push(pmi);
    }
    ctx.patchState({
      input: {
        ...state.input,
        basicOptions: basicOptions,
        specifiedItemOption: undefined,
        adventureActivitiesOption: undefined,
      },
    });
  }

  @Action(QuoteActions.ResetPlanPrices)
  resetPlanPrices(
    ctx: StateContext<IQuoteState>,
    payload: QuoteActions.ResetPlanPrices
  ): void {
    const state = ctx.getState();
    const prices = this.revertPrices(
      payload.planPrices as IQuotePriceSummary[],
      state.prices as IQuotePriceSummary[],
      state.input.planId
    ) as IQuotePriceSummary[];
    ctx.patchState({
      prices: prices,
    });
  }

  @Action(QuoteActions.Purchase)
  purchaseQuote(
    ctx: StateContext<IQuoteState>,
    action: QuoteActions.Purchase
  ): Observable<any> {
    const state = ctx.getState();
    const brandCode = state.brandCode || '';
    const paymentCorrelationId = newGuid();
    const quote = this.quoteHelper.prepareQuoteObject(state, false);
    ctx.patchState({ paymentCorrelationId });

    return this.quoteService
      .purchase(quote, action.payload, brandCode, paymentCorrelationId)
      .pipe(
        tap((purchaseResponse) => {
          ctx.patchState({ ...purchaseResponse.quote });
        }),
        share()
      );
  }

  /**
   * Need to revisit this. There are a couple of potentially dangerous.
   * 1. The fact that it returns an undefined or Observable can cause
   *    unwanted behaviour. We either return the Observable or make it void
   * 2. Part of the logic here should be abstracted to the service.
   * 3. This method is breaking single responsibility
   */
  @Action(QuoteActions.VerifyPurchase)
  verifyPurchase(
    ctx: StateContext<IQuoteState>,
    { response }: QuoteActions.VerifyPurchase
  ): Observable<IPurchaseResponse | NibTravelHttpError> | undefined {
    const threeDSResponseInfo = this.store.selectSnapshot((state) =>
      PaymentState.threeDSResponseInfo(state.payment)
    );
    const verificationType = this.store.selectSnapshot((state) =>
      PaymentState.verification(state.payment)
    ).type;
    const correlationId = this.store.selectSnapshot(
      (state) => state.quote.paymentCorrelationId
    );

    if (verificationType && threeDSResponseInfo) {
      const threeDSRequestInfo = {
        threeDSRequest: this.quoteHelper.getThreeDSRequestData(
          verificationType,
          response,
          threeDSResponseInfo.paymentData
        ),
        threeDSStatus: threeDSResponseInfo.threeDSStatus,
      };

      const quote = ctx.getState();
      const brandCode = quote.brandCode || '';
      //reset payment state
      ctx.dispatch(new PaymentActions.Reset());
      return this.quoteService
        .verifyPurchase(
          quote,
          threeDSRequestInfo,
          threeDSResponseInfo.cardToken,
          threeDSResponseInfo.paymentIds,
          brandCode,
          correlationId
        )
        .pipe(
          tap((purchaseResponse: IPurchaseResponse) => {
            ctx.patchState({ ...purchaseResponse.quote });
          })
        );
    }
    return undefined;
  }

  @Action(QuoteActions.ContinueDuplicatePurchase)
  duplicatePurchase(ctx: StateContext<IQuoteState>): void {
    ctx.setState(
      patch({
        input: patch({
          overrideDuplicateCheck: true,
        } as IQuoteInput),
      })
    );
  }

  @Action(QuoteActions.UpdateFormValue)
  updateFormValue(
    ctx: StateContext<IQuoteState>,
    { value, model }: QuoteActions.UpdateFormValue
  ): void {
    const state = ctx.getState();
    const newValue = this.patchDeepValue(state, model, value);
    ctx.setState(patch(newValue));
  }

  @Action(QuoteActions.UpdateFormStatus)
  updateFormStatus(
    ctx: StateContext<IQuoteState>,
    { payload: { status, path } }: QuoteActions.UpdateFormStatus
  ): void {
    const state = ctx.getState();
    ctx.patchState(setValue(state, `${path}.status`, status));
  }

  @Action(QuoteActions.UpdateFormDirty)
  updateFormDirty(
    ctx: StateContext<IQuoteState>,
    { payload: { dirty, path } }: QuoteActions.UpdateFormDirty
  ): void {
    const state = ctx.getState();
    ctx.patchState(setValue(state, `${path}.dirty`, dirty));
  }

  @Action(QuoteActions.UpdateFormErrors)
  updateFormErrors(
    ctx: StateContext<IQuoteState>,
    { payload: { errors, path } }: QuoteActions.UpdateFormErrors
  ): void {
    const state = ctx.getState();
    ctx.patchState(setValue(state, `${path}.errors`, errors));
  }

  @Action(QuoteActions.ResetForm)
  resetForm(
    ctx: StateContext<IQuoteState>,
    { payload: value }: QuoteActions.ResetForm
  ): void {
    const state = ctx.getState();
    ctx.patchState(merge(state.input, value));
  }

  @Action(QuoteActions.UpdatePolicyHolders)
  updatePolicyHolders(
    ctx: StateContext<IQuoteState>,
    { policyHolders: updatePolicyHolders }: QuoteActions.UpdatePolicyHolders
  ): void {
    const { input, id } = ctx.getState();
    const original = input?.policyHolders ?? [];
    // Merge updated policyHolder information with existing entry
    const policyHolders = updatePolicyHolders.map((item) => {
      const exiting = original.find((ph) => ph.guid === item.guid);
      return {
        ...(exiting ?? {}),
        ...item,
      };
    });

    try {
      this.logPrimaryPolicyHolder(id ?? '', original, policyHolders);
    } catch (ex) {
      this.logger.error({
        category: 'Details',
        method: 'QuoteState.logPrimaryPolicyHolder.Check',
        value: ex,
        context: {
          [LogContextKey.QuoteIdToken]: id,
        },
      });
    }

    ctx.setState(patch({ input: patch({ policyHolders }) }));
  }

  //TODO: Remove this logging once error is fixed
  private logPrimaryPolicyHolder(
    quoteId: string,
    source: IPolicyHoldersInput[],
    dest: IPolicyHoldersInput[]
  ): void {
    const isNullOrEmpty = (value: string | undefined | null): boolean => {
      if (value) {
        return typeof value === 'string' && value.trim().length === 0;
      }
      return true;
    };

    const sourcePrimaryPh = source.find((ph) => ph.isPrimary === true);
    const destPrimaryPh = dest.find((ph) => ph.isPrimary === true);

    if (!sourcePrimaryPh) {
      this.logger.warning({
        category: 'Details',
        method: 'QuoteState.logPrimaryPolicyHolder',
        value: 'Original Primary policyholder not found',
        context: {
          [LogContextKey.QuoteIdToken]: quoteId,
          policyHolderCount: source.length,
          ambiguousCount: source.filter((ph) => ph.isAmbiguous).length,
          dependantCount: source.filter((ph) => ph.isDependent).length,
        },
      });
    }

    if (!destPrimaryPh) {
      this.logger.warning({
        category: 'Details',
        method: 'QuoteState.logPrimaryPolicyHolder',
        value: 'Destination Primary policyholder not found',
        context: {
          [LogContextKey.QuoteIdToken]: quoteId,
          policyHolderCount: dest.length,
          ambiguousCount: dest.filter((ph) => ph.isAmbiguous).length,
          dependantCount: dest.filter((ph) => ph.isDependent).length,
        },
      });
    } else {
      if (isNullOrEmpty(destPrimaryPh.emailAddress)) {
        this.logger.warning({
          category: 'Details',
          method: 'QuoteState.logPrimaryPolicyHolder',
          value: 'Destination Primary policyholder Email is empty',
          context: {
            [LogContextKey.QuoteIdToken]: quoteId,
            guid: destPrimaryPh.guid,
            sourceHadValue: !isNullOrEmpty(sourcePrimaryPh?.emailAddress),
            isAmbiguous: destPrimaryPh.isAmbiguous,
            isDependent: destPrimaryPh.isDependent,
          },
        });
      }
      if (isNullOrEmpty(destPrimaryPh.phoneNumber)) {
        this.logger.warning({
          category: 'Details',
          method: 'QuoteState.logPrimaryPolicyHolder',
          value: 'Destination Primary policyholder phone is empty',
          context: {
            [LogContextKey.QuoteIdToken]: quoteId,
            guid: destPrimaryPh.guid,
            sourceHadValue: !isNullOrEmpty(sourcePrimaryPh?.phoneNumber),
            isAmbiguous: destPrimaryPh.isAmbiguous,
            isDependent: destPrimaryPh.isDependent,
          },
        });
      }
    }
  }

  @Action(QuoteActions.UpdateAddress)
  updateAddress(
    ctx: StateContext<IQuoteState>,
    { address: homeAddress }: QuoteActions.UpdateAddress
  ): void {
    ctx.setState(patch({ input: patch({ homeAddress }) }));
  }

  @Action(QuoteActions.UpdateRegulatoryDeclarations)
  updateRegulatoryDeclarations(
    ctx: StateContext<IQuoteState>,
    { regulatoryDeclarations }: QuoteActions.UpdateRegulatoryDeclarations
  ): void {
    ctx.setState(patch({ input: patch({ regulatoryDeclarations }) }));
  }

  private setQuoteParamsHandler = (
    ctx: StateContext<IQuoteState>,
    { quoteParams }: QuoteActions.SetQuoteParams
  ) => {
    const state = ctx.getState();
    const policyHolders =
      quoteParams?.travelAges?.map((age) => ({ ...emptyPolicyHolder, age })) ??
      [];
    const input = {
      quoteType: quoteParams.quoteType ?? QuoteType.SingleTrip,
      destinationCountryCodes: quoteParams?.destinationCountryCodes,
      departureDate: quoteParams?.departureDate,
      coverEndDate: quoteParams?.coverEndDate,
      campaignCode: quoteParams?.campaignCode ?? state.input.campaignCode,
      partnerCode: quoteParams?.partnerCode ?? state.input.partnerCode,
      policyHolders,
      customerInterfaceId: 1,
      dependentAge: quoteParams.dependentAge,
    } as IQuoteInput;

    //alertMessage: 'quote-expired'
    ctx.setState(patch({ input: patch(input) }));
  };
  @Action(QuoteActions.SetQuoteParams)
  setQuoteParams(
    ctx: StateContext<IQuoteState>,
    { quoteParams }: QuoteActions.SetQuoteParams
  ): void {
    this.setQuoteParamsHandler(ctx, { quoteParams });
  }

  @Action(QuoteActions.SetQuoteParamsToKosmos)
  setQuoteParamsToKosmos(
    ctx: StateContext<IQuoteState>,
    { quoteParams }: QuoteActions.SetQuoteParams
  ): void {
    this.setQuoteParamsHandler(ctx, { quoteParams });
  }

  @Action(QuoteActions.SendEmailQuote)
  sendEmailQuote(
    ctx: StateContext<IQuoteState>,
    { emailAddress }: QuoteActions.SendEmailQuote
  ): Observable<any> {
    const state = ctx.getState();
    const brandCode = this.store.selectSnapshot(AppConfigState.getBrand);
    return this.quoteService
      .emailQuote(state, brandCode, emailAddress)
      .pipe(share());
  }

  @Action(QuoteActions.SendEmailQuote)
  setEmailAddress(
    ctx: StateContext<IQuoteState>,
    { emailAddress }: QuoteActions.SendEmailQuote
  ): void {
    ctx.setState(
      patch({
        input: patch({
          emailAddressForQuote: emailAddress,
        }),
      })
    );
  }

  @Action(QuoteActions.LoadQuotePlans)
  loadQuotePlans(ctx: StateContext<IQuoteState>) {
    const state = ctx.getState();
    const planIds = QuoteState.quotePrices(state)?.map((price) => price.planId);
    const brandCode = this.store.selectSnapshot(AppConfigState.getBrand);
    return planIds?.length > 0
      ? ctx.dispatch(new PlansAction.LoadPlansData(planIds, brandCode))
      : EMPTY;
  }

  @Action(QuoteActions.NavigateQuoteStart)
  navigateQuoteStart(): void {
    this.router.navigate([Route.Quote]);
  }

  @Action(QuoteActions.SetFootprintsData)
  setFootprintsData(
    ctx: StateContext<IQuoteState>,
    { partnerCode, projectId, amount }: QuoteActions.SetFootprintsData
  ): void {
    const footprintsDonation: IFootprintsDonationInput = {
      donationAmount: amount,
      donationProjectId: projectId,
      footprintsAccountCode: partnerCode,
    };

    ctx.setState(patch({ input: patch({ footprintsDonation }) }));
  }

  @Action(QuoteActions.ResetFootprints)
  resetFootprints(ctx: StateContext<IQuoteState>): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { footprintsDonation, ...input } = ctx.getState().input;
    ctx.setState(patch({ input }));
  }

  @Action(QuoteActions.SetTripCancellationOption)
  setTripCancellationOption(
    ctx: StateContext<IQuoteState>,
    { optionType, value }: QuoteActions.SetTripCancellationOption
  ) {
    ctx.setState(
      patch({
        input: patch({
          cancellationOption: {
            optionType: optionType,
            cancellationCoverLimit: value,
          },
          tripValue: value,
        } as IQuoteInput),
      })
    );
  }

  /** This is a temp solution until the backend has the correct handling for empty default cancellation */
  @Action(QuoteActions.SetDefaultCancellationOption)
  setDefaultCancellationOption(ctx: StateContext<IQuoteState>): void {
    ctx.setState(
      patch({
        input: patch({
          cancellationOption: {
            optionType: 'TRIPCANX',
            cancellationCoverLimit: 1000,
          },
        } as IQuoteInput),
      })
    );
  }

  @Action(QuoteActions.SetAnalyticParams)
  setAnalyticParams(
    ctx: StateContext<IQuoteState>,
    { params }: QuoteActions.SetAnalyticParams
  ): void {
    ctx.setState(patch({ analytics: params }));
  }

  private patchDeepValue(state: any, model: string, value: any) {
    const obj: any = { ...state };
    const split = model.split('.');
    const lastIndex = split.length - 1;
    split.reduce((acc, part, index) => {
      if (index === lastIndex) {
        acc[part] = extend({}, acc[part], value);
      } else {
        acc[part] = Array.isArray(acc[part])
          ? acc[part].slice()
          : { ...acc[part] };
      }

      return acc && acc[part];
    }, obj);
    return obj;
  }
}
